/** A Fulfillment Method represents a way in which a restaurant can provide orders to its customers. */
export interface FulfillmentMethod extends FulfillmentMethodMethodOptionsOneOf {
  /** Data specific for pickup fulfillment method. */
  pickupOptions?: PickupInfo;
  /** Data specific for delivery fulfillment method. */
  deliveryOptions?: DeliveryInfo;
  /**
   * Fulfillment method ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * The current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the fulfillment method was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the fulfillment method was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /** Type of fulfillment method. */
  type?: FulfillmentMethodTypeWithLiterals;
  /**
   * Fulfillment method name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string | null;
  /** Whether the fulfillment method is enabled. */
  enabled?: boolean | null;
  /**
   * Fee for using this fulfillment method.
   * @decimalValue options { maxScale:3 }
   */
  fee?: string | null;
  /** Availability of this fulfillment method. */
  availability?: Availability;
  /**
   * Minimum order price to qualify for using this fulfillment method.
   * @decimalValue options { maxScale:3 }
   */
  minOrderPrice?: string | null;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this fulfillment method belongs to.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /** Tags used to classify and sort different types of fulfillment methods. */
  tags?: Tags;
}

/** @oneof */
export interface FulfillmentMethodMethodOptionsOneOf {
  /** Data specific for pickup fulfillment method. */
  pickupOptions?: PickupInfo;
  /** Data specific for delivery fulfillment method. */
  deliveryOptions?: DeliveryInfo;
}

export enum FulfillmentMethodType {
  /** Unknown fulfillment type. */
  UNKNOWN_FULFILLMENT_TYPE = 'UNKNOWN_FULFILLMENT_TYPE',
  /** The customer must pick up the order from the restaurant. */
  PICKUP = 'PICKUP',
  /** The restaurant, or someone on behalf of the restaurant, must deliver the order to the customer. */
  DELIVERY = 'DELIVERY',
}

/** @enumType */
export type FulfillmentMethodTypeWithLiterals =
  | FulfillmentMethodType
  | 'UNKNOWN_FULFILLMENT_TYPE'
  | 'PICKUP'
  | 'DELIVERY';

export interface PickupInfo {
  /**
   * Instructions for the pickup.
   * @maxLength 250
   */
  instructions?: string | null;
  /**
   * Pickup address.
   *
   * This is set to the address of the restaurant.
   * @readonly
   */
  address?: CommonAddress;
}

/** Physical address */
export interface CommonAddress extends CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /** A string containing the full address of this location. */
  formattedAddress?: string | null;
  /** Free text to help find the address. */
  hint?: string | null;
  /** Coordinates of the physical address. */
  geocode?: AddressLocation;
  /** Country full name. */
  countryFullname?: string | null;
  /** Subdivision full name. */
  subdivisionFullname?: string | null;
  /**
   * Multi-level subdivisions from top to bottom.
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /** Apartment number. */
  apt?: string;
  /** Optional address line 1 */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

export interface DeliveryInfo {
  /** Estimated delivery time in minutes. */
  deliveryTimeInMinutes?: number | null;
  /**
   * Threshold for offering free delivery.
   * If the order price exceeds this threshold, the delivery fee is waived.
   * @decimalValue options { maxScale:3 }
   */
  freeDeliveryThreshold?: string | null;
  /** Delivery area supported by this delivery fulfillment method. */
  deliveryArea?: DeliveryArea;
  /**
   * Delivery provider app id.
   * @format GUID
   * @readonly
   */
  deliveryProviderAppId?: string | null;
  /**
   * Pickup instructions for couriers.
   * @maxLength 250
   */
  courierPickupInstructions?: string | null;
}

export interface DeliveryArea extends DeliveryAreaAreaOptionsOneOf {
  /** Settings for a radius delivery area. */
  radiusOptions?: Radius;
  /** Settings for a postal code delivery area. */
  postalCodeOptions?: PostalCode;
  /** Settings for a custom delivery area. */
  customOptions?: CustomArea;
  /** Type of delivery area. */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface DeliveryAreaAreaOptionsOneOf {
  /** Settings for a radius delivery area. */
  radiusOptions?: Radius;
  /** Settings for a postal code delivery area. */
  postalCodeOptions?: PostalCode;
  /** Settings for a custom delivery area. */
  customOptions?: CustomArea;
}

export enum Type {
  /** Unknown delivery area type. */
  UNKNOWN_DELIVERY_AREA = 'UNKNOWN_DELIVERY_AREA',
  /** Delivery area defined by a radius around the restaurant's address. */
  RADIUS = 'RADIUS',
  /** Delivery area defined by a list of postal codes. */
  POSTAL_CODE = 'POSTAL_CODE',
  /** Delivery area defined by a custom polygon. */
  CUSTOM = 'CUSTOM',
  /** Delivery area that is determined by the provider. Setting this option, you must also provide `delivery_provider_app_id`. */
  PROVIDER_DEFINED = 'PROVIDER_DEFINED',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN_DELIVERY_AREA'
  | 'RADIUS'
  | 'POSTAL_CODE'
  | 'CUSTOM'
  | 'PROVIDER_DEFINED';

export interface Radius {
  /**
   * Minimum distance value.
   * The unit of the radius is specified in the `unit` field.
   * @decimalValue options { gte:0, lte:10000, maxScale:3 }
   */
  minDistance?: string | null;
  /**
   * Maximum distance value.
   * The unit of the radius is specified in the `unit` field.
   * @decimalValue options { gte:0, lte:10000, maxScale:3 }
   */
  maxDistance?: string | null;
  /**
   * Address at the center of the circle.
   * @readonly
   */
  centerPointAddress?: CommonAddress;
  /** Unit of measurement of the radius. */
  unit?: UnitWithLiterals;
}

export enum Unit {
  /** Unknown unit. */
  UNKNOWN_UNIT = 'UNKNOWN_UNIT',
  /** Miles. */
  MILES = 'MILES',
  /** Kilometers. */
  KILOMETERS = 'KILOMETERS',
}

/** @enumType */
export type UnitWithLiterals = Unit | 'UNKNOWN_UNIT' | 'MILES' | 'KILOMETERS';

export interface PostalCode {
  /**
   * Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.
   * @format COUNTRY
   * @readonly
   */
  countryCode?: string | null;
  /**
   * List of postal codes and postal code regexes. For example, `10001`, `10002` or `1000*`.
   * A postal code regex will enable you to define a range of postal codes using an asterisk (*).
   * For example, to include the postal codes in the range of `10001`-`10009`, use `1000*`.
   * @maxLength 20
   * @minLength 1
   * @maxSize 100
   */
  postalCodes?: string[] | null;
}

export interface CustomArea {
  /** Geocodes of the polygon defining the delivery area. */
  geocodes?: AddressLocation[];
}

export interface Availability {
  /** A list of availability times for the days of the week. */
  availableTimes?: DayOfWeekAvailability[];
  /**
   * The timezone in which the availability times are given.
   * @readonly
   */
  timeZone?: string | null;
}

export interface DayOfWeekAvailability {
  /** The day of week this availability relates to. */
  dayOfWeek?: EntitiesDayOfWeekWithLiterals;
  /** A list of time ranges during which the fulfillment should be available. */
  timeRanges?: TimeOfDayRange[];
}

export enum EntitiesDayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

/** @enumType */
export type EntitiesDayOfWeekWithLiterals =
  | EntitiesDayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

export interface TimeOfDayRange {
  /** The start time in time of day representation. */
  startTime?: TimeOfDay;
  /** The end time in time of day representation. */
  endTime?: TimeOfDay;
}

export interface TimeOfDay {
  /**
   * Hours. <br />
   * Min: `0`. <br />
   * Max: `23`.
   */
  hours?: number;
  /**
   * Minutes. <br />
   * Min: `0`. <br />
   * Max: `23`.
   */
  minutes?: number;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface DineInInfo extends DineInInfoAdditionalInfoLabelOptionsOneOf {
  /** Options for AdditionalInfoLabel. Required when `type` is `CUSTOM`. */
  customOptions?: CustomConfig;
  /**
   * Instructions for dine-in orders.
   * @maxLength 250
   */
  customerInstructions?: string | null;
  /**
   * Label for an additional information field shown to customers when placing a dine-in order.
   * Use this when you want to collect optional details (e.g., "Table number", "Room number").
   * If no additional information is needed, leave this field empty.
   * Defines how customers provide information about their seating place when placing a dine-in order.
   * This determines whether customers need to specify their seating place and how they input it.
   */
  additionalInfoLabel?: AdditionalInfoLabelWithLiterals;
  /**
   * Dine-in address. This is the restaurant's address.
   * @readonly
   */
  address?: CommonAddress;
}

/** @oneof */
export interface DineInInfoAdditionalInfoLabelOptionsOneOf {
  /** Options for AdditionalInfoLabel. Required when `type` is `CUSTOM`. */
  customOptions?: CustomConfig;
}

export enum AdditionalInfoLabel {
  /** No identification required. Customers don't need to specify their seating place. */
  NO_ADDITIONAL_INFO_LABEL = 'NO_ADDITIONAL_INFO_LABEL',
  /** Customers select their table number from a predefined list of tables available in the restaurant. */
  TABLE_NUMBER = 'TABLE_NUMBER',
  /** Customers select their table number from a predefined list of tables available in the restaurant. */
  ROOM_NUMBER = 'ROOM_NUMBER',
  /** Customers can enter any custom text or number to identify their seating place (e.g. booth, area, room, etc.). */
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type AdditionalInfoLabelWithLiterals =
  | AdditionalInfoLabel
  | 'NO_ADDITIONAL_INFO_LABEL'
  | 'TABLE_NUMBER'
  | 'ROOM_NUMBER'
  | 'CUSTOM';

export interface CustomConfig {
  /** @maxLength 40 */
  customInfo?: string | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateFulfillmentMethodRequest {
  /** Fulfillment method to create. */
  fulfillmentMethod: FulfillmentMethod;
}

export interface CreateFulfillmentMethodResponse {
  /** The created fulfillment method. */
  fulfillmentMethod?: FulfillmentMethod;
}

export interface BulkCreateFulfillmentMethodsRequest {
  /**
   * Fulfillment methods to create.
   * @maxSize 100
   */
  fulfillmentMethods?: FulfillmentMethod[];
  /** If true, the created entities will be returned. */
  returnEntity?: boolean;
}

export interface BulkCreateFulfillmentMethodsResponse {
  /** Information about the created fulfillment method. */
  results?: BulkCreateFulfillmentMethodResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateFulfillmentMethodResult {
  /** Metadata for fulfillment method creation. */
  itemMetadata?: ItemMetadata;
  /** Created fulfillment method. */
  fulfillmentMethod?: FulfillmentMethod;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetFulfillmentMethodRequest {
  /**
   * The ID of the fulfillment method to retrieve.
   * @format GUID
   */
  fulfillmentMethodId: string;
}

export interface GetFulfillmentMethodResponse {
  /** The retrieved fulfillment method. */
  fulfillmentMethod?: FulfillmentMethod;
}

export interface UpdateFulfillmentMethodRequest {
  /**
   * Fulfillment method to update.
   * The fulfillment method update may be partial with the use of `field_mask`.
   */
  fulfillmentMethod: FulfillmentMethod;
}

export interface UpdateFulfillmentMethodResponse {
  /** The updated fulfillment method. */
  fulfillmentMethod?: FulfillmentMethod;
}

export interface DeleteFulfillmentMethodRequest {
  /**
   * The ID of the fulfillment method to delete.
   * @format GUID
   */
  fulfillmentMethodId: string;
}

export interface DeleteFulfillmentMethodResponse {}

export interface QueryFulfillmentMethodsRequest {
  /** The query by which to select fulfillment methods. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 64
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryFulfillmentMethodsResponse {
  /** The retrieved fulfillment methods. */
  fulfillmentMethods?: FulfillmentMethod[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface ListFulfillmentMethodsRequest {
  /** Cursor paging */
  cursorPaging?: CursorPaging;
}

export interface ListFulfillmentMethodsResponse {
  /** The retrieved fulfillment methods. */
  fulfillmentMethods?: FulfillmentMethod[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface ListAvailableFulfillmentMethodsForAddressRequest {
  /** The address by which to filter delivery fulfillment methods. */
  address?: CommonAddress;
  /** Cursor paging */
  cursorPaging?: CursorPaging;
  /**
   * If provided, only fulfillment methods with the given IDs will be returned.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds?: string[];
}

export interface ListAvailableFulfillmentMethodsForAddressResponse {
  /** The retrieved fulfillment methods. */
  fulfillmentMethods?: FulfillmentMethod[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface ListActiveFulfillmentMethodsRequest {
  /**
   * Only fulfillment methods with the given IDs will be returned.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds?: string[];
  /** Cursor paging */
  cursorPaging?: CursorPaging;
}

export interface ListActiveFulfillmentMethodsResponse {
  /** The enabled fulfillment methods. */
  fulfillmentMethods?: FulfillmentMethod[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface GetAccumulatedFulfillmentMethodsAvailabilityRequest {
  /**
   * fulfillment method ids to check availability for.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds?: string[];
}

export interface GetAccumulatedFulfillmentMethodsAvailabilityResponse {
  /** The accumulated availability of all fulfillment methods. */
  availability?: Availability;
  /** Fulfillment methods types that accumulate availability. */
  types?: FulfillmentMethodTypeWithLiterals[];
}

export interface GetCombinedMethodAvailabilityRequest {
  /**
   * IDs of fulfillment methods used to determine the combined availability.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds: string[];
}

export interface GetCombinedMethodAvailabilityResponse {
  /** The combined availability of the given fulfillment methods. */
  combinedAvailability?: Availability;
  /**
   * Types of fulfillment methods available during at least some the combined availability's `availableTimes`.
   * @maxSize 100
   */
  fulfillmentTypes?: FulfillmentMethodTypeWithLiterals[];
}

export interface GetAggregatedMethodAvailabilityRequest {
  /**
   * IDs of fulfillment methods used to determine the aggregated availability.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds: string[];
}

export interface GetAggregatedMethodAvailabilityResponse {
  /** The aggregated availability of the given fulfillment methods. */
  aggregatedAvailability?: Availability;
  /**
   * Types of fulfillment methods available during at least some the aggregated availability's `availableTimes`.
   * @maxSize 100
   */
  fulfillmentTypes?: FulfillmentMethodTypeWithLiterals[];
}

export interface BulkUpdateFulfillmentMethodTagsRequest {
  /**
   * IDs of the fulfillment methods to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  fulfillmentMethodIds: string[];
  /** Tags to assign to the fulfillment methods. */
  assignTags?: Tags;
  /** Tags to unassign from the fulfillment methods. */
  unassignTags?: Tags;
}

export interface BulkUpdateFulfillmentMethodTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateFulfillmentMethodTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: CommonBulkActionMetadata;
}

export interface CommonItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface BulkUpdateFulfillmentMethodTagsResult {
  /** Metadata for the updated fulfillment method. */
  itemMetadata?: CommonItemMetadata;
}

export interface CommonBulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateFulfillmentMethodTagsByFilterRequest {
  /** Filter that determines which fulfillment methods to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the fulfillment methods. */
  assignTags?: Tags;
  /** Tags to unassign from the fulfillment methods. */
  unassignTags?: Tags;
}

export interface BulkUpdateFulfillmentMethodTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface Empty {}

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateFulfillmentMethodTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateFulfillmentMethodTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
